import * as React from 'react'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ArrowLeft,
  MoreVertical,
  Clock,
  User,
  Phone,
  Archive,
  XCircle,
  Loader2,
  Bot,
  RotateCcw,
  ChevronDown,
  MailOpen,
} from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  useWAMessages,
  useMarkWAAsRead,
  useMarkWAAsUnread,
  useUpdateWAConversation,
} from '@/hooks/useWhatsApp'
import { ChatBubble } from './chat-bubble'
import { ChatInput } from './chat-input'
import type { WAConversation, WAMessage } from '@/lib/api-client'

interface ChatViewProps {
  conversation: WAConversation
  onBack: () => void
}

export function ChatView({ conversation, onBack }: ChatViewProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const bottomRef = React.useRef<HTMLDivElement>(null)

  // Fetch messages
  const {
    data: messagesData,
    isPending,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useWAMessages(conversation.id)

  // Mutations
  const markAsRead = useMarkWAAsRead()
  const markAsUnread = useMarkWAAsUnread()
  const updateConversation = useUpdateWAConversation()

  // Flatten messages from infinite query pages
  // Pages are loaded newest-first, so reverse page order for chronological display
  const messages = React.useMemo(() => {
    if (!messagesData?.pages) return []
    return [...messagesData.pages].reverse().flatMap((page) => page.data)
  }, [messagesData])

  // Mark as read once when a conversation is first opened. Keyed by id via a
  // ref so a later "Mark as unread" (which bumps unreadCount back up) is NOT
  // immediately undone — the effect won't re-fire for the same open thread.
  const autoReadForRef = React.useRef<number | null>(null)
  React.useEffect(() => {
    if (autoReadForRef.current === conversation.id) return
    autoReadForRef.current = conversation.id
    if (conversation.unreadCount > 0) {
      markAsRead.mutate(conversation.id)
    }
  }, [conversation.id, conversation.unreadCount])

  // Scroll state tracking
  const prevMessageCountRef = React.useRef(0)
  const isLoadingOlderRef = React.useRef(false)
  const prevScrollHeightRef = React.useRef(0)
  const [isAtBottom, setIsAtBottom] = React.useState(true)
  const [newMessageCount, setNewMessageCount] = React.useState(0)

  // Check if scrolled to bottom (within 100px threshold)
  const checkIsAtBottom = React.useCallback(() => {
    if (!scrollRef.current) return true
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    return scrollHeight - scrollTop - clientHeight < 100
  }, [])

  // Reset new message count when conversation changes
  React.useEffect(() => {
    prevMessageCountRef.current = 0
    setNewMessageCount(0)
    setIsAtBottom(true)
  }, [conversation.id])

  // Scroll to bottom only when appropriate (WhatsApp Web behavior)
  React.useEffect(() => {
    const count = messages.length
    const prev = prevMessageCountRef.current

    if (count > 0 && prev === 0) {
      // Initial load — scroll to bottom instantly
      bottomRef.current?.scrollIntoView()
      setIsAtBottom(true)
    } else if (count > prev && !isLoadingOlderRef.current) {
      // New message(s) arrived
      const addedCount = count - prev
      if (isAtBottom) {
        // User is at bottom — auto-scroll like WhatsApp Web
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
      } else {
        // User is reading old messages — DON'T scroll, show count on floating button
        setNewMessageCount((c) => c + addedCount)
      }
    }

    prevMessageCountRef.current = count
  }, [messages.length, isAtBottom])

  // Preserve scroll position when older messages are prepended
  React.useEffect(() => {
    if (isLoadingOlderRef.current && scrollRef.current) {
      const newScrollHeight = scrollRef.current.scrollHeight
      const addedHeight = newScrollHeight - prevScrollHeightRef.current
      scrollRef.current.scrollTop += addedHeight
      isLoadingOlderRef.current = false
    }
  }, [messages])

  // Handle scroll — infinite loading + track "at bottom" state
  const handleScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement

    // Track if user is at bottom
    const atBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 100
    setIsAtBottom(atBottom)

    // Clear new message count when user scrolls back to bottom
    if (atBottom) {
      setNewMessageCount(0)
    }

    // Load older messages when scrolled near top
    if (target.scrollTop < 100 && hasNextPage && !isFetchingNextPage) {
      isLoadingOlderRef.current = true
      prevScrollHeightRef.current = target.scrollHeight
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Scroll to bottom handler (for floating button)
  const scrollToBottom = React.useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    setNewMessageCount(0)
  }, [])

  // Get display info
  const { contact, patient, window, branch } = conversation
  const displayName = patient?.name || contact.displayName || formatPhone(contact.waPhoneNumber)
  const initials = getInitials(displayName)

  // Actions
  const handleArchive = () => {
    updateConversation.mutate({ id: conversation.id, data: { status: 'archived' } })
  }
  const handleClose = () => {
    updateConversation.mutate({ id: conversation.id, data: { status: 'closed' } })
  }
  const handleReopen = () => {
    updateConversation.mutate({ id: conversation.id, data: { status: 'open' } })
  }
  const handleMarkUnread = () => {
    // Flag back to unread, then step out so the badge is visible to colleagues.
    markAsUnread.mutate(conversation.id, { onSuccess: () => onBack() })
  }
  const handleAiToggle = (enabled: boolean) => {
    updateConversation.mutate({ id: conversation.id, data: { aiAutoReply: enabled } })
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 h-[59px] border-b border-[#e9edef] bg-[#f0f2f5]">
        {/* Back button (mobile) */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="lg:hidden -ml-2 h-8 w-8"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>

        {/* Avatar */}
        <Avatar className="w-10 h-10">
          <AvatarImage src={contact.profilePictureUrl || undefined} alt={displayName} />
          <AvatarFallback className="bg-[#dfe5e7] text-[#54656f] text-sm font-semibold">
            {initials}
          </AvatarFallback>
        </Avatar>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-[15px] text-[#111b21] truncate">
              {displayName}
            </h2>
            {branch && (
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px] h-4 font-medium">
                {branch.shortName || branch.name}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[12px] text-[#667781]">
            {patient && (
              <>
                <User className="w-3 h-3" />
                <span>{patient.pno}</span>
                <span className="text-border">|</span>
              </>
            )}
            <Phone className="w-3 h-3" />
            <span>{formatPhone(contact.waPhoneNumber)}</span>
          </div>
        </div>

        {/* Right side controls */}
        <div className="flex items-center gap-2">
          {/* AI Toggle */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors',
                  conversation.aiAutoReply
                    ? 'border-info/30 bg-info/5'
                    : 'border-border bg-muted/50'
                )}>
                  <Bot className={cn(
                    'w-3.5 h-3.5',
                    conversation.aiAutoReply ? 'text-info' : 'text-text-muted'
                  )} />
                  <Switch
                    checked={conversation.aiAutoReply ?? false}
                    onCheckedChange={handleAiToggle}
                    className="h-4 w-7"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>AI Auto-Reply {conversation.aiAutoReply ? 'On' : 'Off'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Window indicator */}
          {window.isOpen && window.formatted && (
            <div className="hidden sm:flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#00a884]/10 text-[#00a884] text-[11px] font-medium">
              <Clock className="w-3 h-3" />
              {window.formatted}
            </div>
          )}

          {/* Actions menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleMarkUnread} disabled={markAsUnread.isPending}>
                <MailOpen className="w-4 h-4 mr-2" />
                Mark as unread
              </DropdownMenuItem>
              {conversation.status !== 'archived' && (
                <DropdownMenuItem onClick={handleArchive}>
                  <Archive className="w-4 h-4 mr-2" />
                  Archive
                </DropdownMenuItem>
              )}
              {conversation.status !== 'closed' && (
                <DropdownMenuItem onClick={handleClose}>
                  <XCircle className="w-4 h-4 mr-2" />
                  Close
                </DropdownMenuItem>
              )}
              {(conversation.status === 'archived' || conversation.status === 'closed') && (
                <DropdownMenuItem onClick={handleReopen}>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reopen
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Messages area - WhatsApp dark background */}
      <div
        className="flex-1 min-h-0 overflow-y-auto flex flex-col px-[3%] sm:px-[5%] lg:px-[7%] py-3"
        onScroll={handleScroll}
        ref={scrollRef}
        style={{
          backgroundColor: '#efeae2',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='400' height='400' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='p' width='40' height='40' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='20' cy='20' r='0.8' fill='%23d1cdc7' opacity='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='400' height='400' fill='url(%23p)'/%3E%3C/svg%3E")`,
        }}
      >
        {/* Spacer pushes messages to bottom when content is short */}
        <div className="flex-1" />
        <div>
          {/* Load more indicator */}
          {isFetchingNextPage && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-[#667781]" />
            </div>
          )}

          {isPending ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="mb-3">
                <MessageSkeleton isOutgoing={i % 2 === 1} />
              </div>
            ))
          ) : isError ? (
            <div className="p-8 text-center">
              <p className="text-sm text-destructive">Failed to load messages</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mb-3">
                <Bot className="w-5 h-5 text-[#667781]" />
              </div>
              <p className="text-sm text-[#667781]">No messages yet</p>
              <p className="text-xs text-[#667781]/60 mt-1">Messages will appear here</p>
            </div>
          ) : (
            messages.map((message, index) => {
              const prev = messages[index - 1]
              const next = messages[index + 1]
              const sameSenderAsPrev = prev && prev.direction === message.direction && !shouldShowDate(message, prev) && timeDiffMinutes(prev, message) < 1
              const sameSenderAsNext = next && next.direction === message.direction && !shouldShowDate(next, message) && timeDiffMinutes(message, next) < 1

              return (
                <div
                  key={message.id}
                  className={cn(
                    sameSenderAsNext ? 'mb-[2px]' : 'mb-3'
                  )}
                >
                  <ChatBubble
                    message={message}
                    showDate={shouldShowDate(message, prev)}
                    isFirstInGroup={!sameSenderAsPrev}
                    isLastInGroup={!sameSenderAsNext}
                  />
                </div>
              )
            })
          )}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* Floating scroll-to-bottom button (WhatsApp Web style) */}
      {!isAtBottom && !isPending && (
        <div className="relative">
          <button
            type="button"
            onClick={scrollToBottom}
            className="absolute bottom-3 right-5 z-10 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center transition-all duration-200 hover:bg-[#f0f2f5] active:scale-95"
          >
            {newMessageCount > 0 && (
              <span className="absolute -top-2 -right-1 min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#25d366] text-white text-[11px] font-bold flex items-center justify-center">
                {newMessageCount > 99 ? '99+' : newMessageCount}
              </span>
            )}
            <ChevronDown className="w-5 h-5 text-[#667781]" />
          </button>
        </div>
      )}

      {/* Input */}
      <ChatInput
        conversationId={conversation.id}
        windowOpen={window.isOpen}
      />
    </div>
  )
}

// Helper functions

function formatPhone(phone: string): string {
  if (phone.startsWith('60') && phone.length >= 11) {
    const country = phone.slice(0, 2)
    const prefix = phone.slice(2, 4)
    const mid = phone.slice(4, 7)
    const last = phone.slice(7)
    return `+${country} ${prefix}-${mid} ${last}`
  }
  return phone
}

function getInitials(name: string): string {
  const parts = name.split(' ').filter(Boolean)
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return ((parts[0][0] || '') + (parts[1][0] || '')).toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

function shouldShowDate(current: WAMessage, previous?: WAMessage): boolean {
  if (!previous) return true
  return new Date(current.createdAt).toDateString() !== new Date(previous.createdAt).toDateString()
}

function timeDiffMinutes(a: WAMessage, b: WAMessage): number {
  return Math.abs(new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) / 60000
}

function MessageSkeleton({ isOutgoing }: { isOutgoing: boolean }) {
  return (
    <div className={cn('flex', isOutgoing ? 'justify-end' : 'justify-start')}>
      <div className={cn(
        'rounded-lg p-3 max-w-[65%] shadow-sm',
        isOutgoing ? 'bg-[#d9fdd3]/60' : 'bg-white'
      )}>
        <Skeleton className={cn('h-4 mb-1.5 rounded', isOutgoing ? 'w-28' : 'w-40')} />
        <Skeleton className={cn('h-3 rounded', isOutgoing ? 'w-16' : 'w-20')} />
      </div>
    </div>
  )
}
